/**
 * @fileoverview Document Thumbnail Component
 * 
 * Displays a thumbnail preview of a document (primarily PDFs).
 * Uses client-side PDF.js rendering for PDF thumbnails.
 * Backend provides presigned URLs, frontend renders the thumbnail.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Skeleton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  PiFilePdf,
  PiImage,
  PiFileText,
} from 'react-icons/pi';
import { apiService } from '../services/apiService';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - use unpkg which hosts all npm versions
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface DocumentThumbnailProps {
  /** S3 URI of the document */
  s3Uri: string;
  /** Filename for tooltip display */
  fileName?: string;
  /** Width of the thumbnail */
  width?: number;
  /** Height of the thumbnail */
  height?: number;
  /** Click handler */
  onClick?: () => void;
  /** Whether to show filename below thumbnail */
  showFileName?: boolean;
}

interface ThumbnailCache {
  [key: string]: {
    dataUrl: string | null;
    timestamp: number;
    error?: boolean;
  };
}

// Global thumbnail cache (persists across component instances)
const thumbnailCache: ThumbnailCache = {};
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// File type detection helpers
const isPdf = (uri: string): boolean => uri.toLowerCase().endsWith('.pdf');
const isImage = (uri: string): boolean => {
  const lowerUri = uri.toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.tiff', '.tif'].some(ext => lowerUri.endsWith(ext));
};
const isSupportedType = (uri: string): boolean => isPdf(uri) || isImage(uri);

// Get appropriate icon for file type
const getFileIcon = (uri: string, size: number) => {
  if (isPdf(uri)) return <PiFilePdf size={size} style={{ color: '#d32f2f' }} />;
  if (isImage(uri)) return <PiImage size={size} style={{ color: '#1976d2' }} />;
  return <PiFileText size={size} style={{ color: '#757575' }} />;
};

const getFileLabel = (uri: string): string => {
  if (isPdf(uri)) return 'PDF';
  if (isImage(uri)) return 'Image';
  return 'Doc';
};

export const DocumentThumbnail: React.FC<DocumentThumbnailProps> = ({
  s3Uri,
  fileName,
  width = 80,
  height = 100,
  onClick,
  showFileName = false,
}) => {
  const [thumbnailDataUrl, setThumbnailDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Extract filename from S3 URI if not provided
  const displayName = fileName || s3Uri.split('/').pop() || 'Document';

  /**
   * Render PDF first page to canvas and get data URL
   */
  const renderPdfThumbnail = async (pdfUrl: string): Promise<string> => {
    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({
      url: pdfUrl,
      // Disable range requests which can cause CORS issues
      disableRange: true,
      disableStream: true,
    });
    
    const doc = await loadingTask.promise;
    
    if (doc.numPages === 0) {
      throw new Error('PDF has no pages');
    }

    // Get first page
    const page = await doc.getPage(1);
    const viewport = page.getViewport({ scale: 1.0 });
    
    // Calculate scale to fit thumbnail dimensions while maintaining aspect ratio
    const scaleX = width / viewport.width;
    const scaleY = height / viewport.height;
    const scale = Math.min(scaleX, scaleY);
    
    const scaledViewport = page.getViewport({ scale });
    
    // Create offscreen canvas
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(scaledViewport.width);
    canvas.height = Math.floor(scaledViewport.height);
    
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get canvas context');
    }
    
    // White background
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Render PDF page to canvas
    await page.render({
      canvasContext: context,
      viewport: scaledViewport,
    }).promise;
    
    // Convert to data URL
    return canvas.toDataURL('image/png');
  };

  const fetchThumbnail = useCallback(async () => {
    // Skip non-supported file types
    if (!isSupportedType(s3Uri)) {
      setError(true);
      return;
    }

    // Check cache first
    const cached = thumbnailCache[s3Uri];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (cached.error) {
        setError(true);
      } else if (cached.dataUrl) {
        setThumbnailDataUrl(cached.dataUrl);
      }
      return;
    }

    setLoading(true);
    setError(false);

    try {
      // Get presigned URL from backend
      const encodedUri = encodeURIComponent(s3Uri);
      const data = await apiService.get(`/api/thumbnails?s3Uri=${encodedUri}`);
      
      if (data.imageUrl) {
        // For images, use the presigned URL directly
        setThumbnailDataUrl(data.imageUrl);
        thumbnailCache[s3Uri] = {
          dataUrl: data.imageUrl,
          timestamp: Date.now(),
        };
      } else if (data.pdfUrl) {
        // Render PDF client-side
        const dataUrl = await renderPdfThumbnail(data.pdfUrl);
        setThumbnailDataUrl(dataUrl);
        thumbnailCache[s3Uri] = {
          dataUrl,
          timestamp: Date.now(),
        };
      } else {
        setError(true);
        thumbnailCache[s3Uri] = {
          dataUrl: null,
          timestamp: Date.now(),
          error: true,
        };
      }
    } catch (err) {
      console.error('Failed to generate thumbnail:', err);
      setError(true);
      thumbnailCache[s3Uri] = {
        dataUrl: null,
        timestamp: Date.now(),
        error: true,
      };
    } finally {
      setLoading(false);
    }
  }, [s3Uri, width, height]);

  useEffect(() => {
    fetchThumbnail();
  }, [fetchThumbnail]);

  const renderContent = () => {
    if (loading) {
      return (
        <Skeleton
          variant="rectangular"
          width={width}
          height={height}
          animation="wave"
          sx={{ borderRadius: 1 }}
        />
      );
    }

    if (error || !thumbnailDataUrl) {
      // Show appropriate file type icon as fallback
      return (
        <Box
          sx={{
            width,
            height,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            border: '1px solid',
            borderColor: '#e0e0e0',
            gap: 0.5,
          }}
        >
          {getFileIcon(s3Uri, Math.min(width, height) * 0.4)}
          <Box sx={{ fontSize: '0.6rem', color: 'text.secondary', textAlign: 'center', px: 0.5 }}>
            {getFileLabel(s3Uri)}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={thumbnailDataUrl}
        alt={displayName}
        sx={{
          width,
          height,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.300',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': onClick ? {
            transform: 'scale(1.05)',
            boxShadow: 2,
          } : undefined,
        }}
      />
    );
  };

  return (
    <Tooltip title={displayName} placement="top">
      <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: onClick ? 'pointer' : 'default',
          p: 0.5,
          backgroundColor: 'transparent',
        }}
      >
        {renderContent()}
        {showFileName && (
          <Box
            sx={{
              mt: 0.5,
              fontSize: '0.7rem',
              color: 'text.secondary',
              maxWidth: width,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {displayName.length > 15 
              ? displayName.substring(0, 12) + '...' 
              : displayName}
          </Box>
        )}
      </Paper>
    </Tooltip>
  );
};

/**
 * Batch prefetch thumbnails for multiple documents
 * Pre-fetches and caches thumbnails for display
 */
export const prefetchThumbnails = async (s3Uris: string[]): Promise<void> => {
  // Filter out cached and unsupported URIs
  const uncachedUris = s3Uris.filter(uri => {
    const cached = thumbnailCache[uri];
    return isSupportedType(uri) && (!cached || Date.now() - cached.timestamp >= CACHE_DURATION);
  });

  if (uncachedUris.length === 0) return;

  try {
    const data = await apiService.post('/api/thumbnails/batch', { s3Uris: uncachedUris });
    // Note: For client-side rendering, we just store the pdfUrl
    // Actual rendering happens on component mount
    data.thumbnails?.forEach((result: { s3Uri: string; pdfUrl?: string; error?: string }) => {
      if (result.error) {
        thumbnailCache[result.s3Uri] = {
          dataUrl: null,
          timestamp: Date.now(),
          error: true,
        };
      }
      // Don't render here - let the component render on mount
    });
  } catch (err) {
    console.error('Failed to batch fetch thumbnail URLs:', err);
  }
};

/**
 * Clear the thumbnail cache
 */
export const clearThumbnailCache = (): void => {
  Object.keys(thumbnailCache).forEach(key => delete thumbnailCache[key]);
};

export default DocumentThumbnail;
